import { useMemo, useState } from "react";
import { Link as Anchor } from "lucide-react";
import type { CockpitCaseSummary } from "@/lib/api";

type SortBy = "deadline" | "risk" | "progress";

interface Props {
  cases: CockpitCaseSummary[];
  selectedCaseId: number | null;
  onSelect: (caseId: number) => void;
}

function urgencyColor(daysRemaining: number | null): string {
  if (daysRemaining === null) return "var(--arbitre-slate-500)";
  if (daysRemaining <= 7) return "var(--block-red)";
  if (daysRemaining <= 14) return "var(--alert-orange)";
  if (daysRemaining <= 30) return "var(--proof-gold)";
  return "var(--arbitre-slate-300)";
}

function statusLabel(statut: string): string {
  const map: Record<string, string> = {
    BROUILLON: "Brouillon",
    OUVERT: "Ouvert",
    INSTRUCTION: "Instruction",
    SENTENCE_RENDUE: "Sentence rendue",
    CLOTURE: "Cloture",
    TERMINE: "Termine",
  };
  return map[statut.toUpperCase()] ?? statut;
}

export function PrioritizedCaseList({
  cases,
  selectedCaseId,
  onSelect,
}: Props) {
  const [sortBy, setSortBy] = useState<SortBy>("deadline");
  const [urgentOnly, setUrgentOnly] = useState(false);

  const sorted = useMemo(() => {
    let list = [...cases];
    if (urgentOnly) {
      list = list.filter(
        (c) => c.days_remaining !== null && c.days_remaining <= 14,
      );
    }
    list.sort((a, b) => {
      if (sortBy === "deadline") {
        return (
          (a.days_remaining ?? Number.POSITIVE_INFINITY) -
          (b.days_remaining ?? Number.POSITIVE_INFINITY)
        );
      }
      if (sortBy === "risk") {
        return (b.risk_score ?? 0) - (a.risk_score ?? 0);
      }
      return (b.progress_percentage ?? 0) - (a.progress_percentage ?? 0);
    });
    return list;
  }, [cases, sortBy, urgentOnly]);

  return (
    <div className="space-y-3">
      <div
        className="flex items-center justify-between gap-3 rounded-md p-2.5"
        style={{ background: "var(--graphite-700)" }}
      >
        <label
          className="flex items-center gap-2 text-xs"
          style={{ color: "var(--arbitre-slate-300)" }}
        >
          Trier&nbsp;:
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
            className="rounded-md px-2 py-1 text-xs"
            style={{
              background: "var(--graphite-800)",
              color: "var(--arbitre-slate-100)",
              border: "1px solid var(--graphite-600)",
            }}
          >
            <option value="deadline">Echeance</option>
            <option value="risk">Score de risque</option>
            <option value="progress">Progression</option>
          </select>
        </label>
        <label
          className="inline-flex items-center gap-2 text-xs cursor-pointer"
          style={{ color: "var(--arbitre-slate-300)" }}
        >
          <input
            type="checkbox"
            checked={urgentOnly}
            onChange={(e) => setUrgentOnly(e.target.checked)}
          />
          Urgents uniquement
        </label>
      </div>

      {sorted.length === 0 ? (
        <p
          className="text-xs italic text-center py-4"
          style={{ color: "var(--arbitre-slate-500)" }}
        >
          Aucun dossier {urgentOnly ? "urgent" : "actif"}.
        </p>
      ) : (
        <ul className="space-y-2">
          {sorted.map((c) => {
            const color = urgencyColor(c.days_remaining);
            const isSelected = c.id === selectedCaseId;
            const progress = c.progress_percentage ?? 0;
            return (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => onSelect(c.id)}
                  className="gsf-surface w-full text-left p-3 hover:brightness-110 transition"
                  style={{
                    borderLeft: `3px solid ${color}`,
                    background: isSelected
                      ? "color-mix(in oklch, var(--proof-gold) 8%, var(--graphite-800))"
                      : undefined,
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div
                        className="gsf-mono-proof text-xs"
                        style={{ color: "var(--proof-gold)" }}
                      >
                        {c.reference}
                      </div>
                      <div
                        className="text-sm mt-0.5 truncate"
                        style={{ color: "var(--arbitre-slate-100)" }}
                      >
                        {c.objet_litige}
                      </div>
                    </div>
                    <span
                      className="shrink-0 rounded-full px-2 py-0.5 text-[10px]"
                      style={{
                        background: "var(--graphite-700)",
                        color: "var(--arbitre-slate-300)",
                      }}
                    >
                      {statusLabel(c.statut)}
                    </span>
                  </div>

                  <div
                    className="flex items-center gap-4 mt-2 text-[11px]"
                    style={{ color: "var(--arbitre-slate-300)" }}
                  >
                    <span style={{ color }}>
                      {c.days_remaining !== null
                        ? `${c.days_remaining}j restants`
                        : "Pas d'echeance"}
                    </span>
                    <div className="flex items-center gap-1.5 flex-1 min-w-[60px]">
                      <div
                        className="h-1 rounded-full flex-1 overflow-hidden"
                        style={{ background: "var(--graphite-600)" }}
                      >
                        <div
                          className="h-full"
                          style={{
                            width: `${progress}%`,
                            background: "var(--proof-gold)",
                          }}
                        />
                      </div>
                      <span style={{ color: "var(--proof-gold)" }}>
                        {progress.toFixed(0)}%
                      </span>
                    </div>
                    {c.anchored_status === "SUCCESS" && (
                      <span
                        className="inline-flex items-center gap-1"
                        style={{ color: "var(--proof-gold)" }}
                        title="Ancrage blockchain confirme"
                      >
                        <Anchor size={11} />
                        ancre
                      </span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
